/**
 * ThreadV5MobileShell — FXC-8939.
 *
 * Below ~768px the desktop topbar + left nav rail are replaced by a
 * mobile-first shell: a compact header and a bottom tab bar
 * (Home / Plots / Runs / Ask). The surface + drawers + focus layer render
 * full-bleed between them as `children`, so the same workbench data,
 * plot-point handler, and provenance focus layer drive both shells —
 * desktop and the anonymous /public share recipient alike.
 *
 * Mobile is for consuming and interrogating, not building: the account-only
 * affordances (Flow360 connect, board editing, members) are intentionally
 * absent here. This is increment 1 — the shell skeleton + tab routing;
 * the tap-a-point provenance sheet, legend filter chip, and DAG step-timeline
 * land in follow-up increments.
 */

import type { ReactNode } from "react";
import { ThemeToggle } from "../../components/toolbar/ThemeToggle";
import { Icons } from "../thread-v4/icons";
// Type-only import — erased at build time, so no runtime cycle with the page.
import type { V5View } from "./ThreadV5Workbench";

export type V5MobileTab = "home" | "plots" | "runs" | "ask";

interface ThreadV5MobileShellProps {
  projectName: string;
  activeView: V5View;
  chatOpen: boolean;
  readOnly: boolean;
  /** Present only when realtime chat is available (authed, live project). */
  onOpenChat?: () => void;
  onOpenView: (view: V5View) => void;
  onSignIn: () => void;
  children: ReactNode;
}

const TAB_TO_VIEW: Record<Exclude<V5MobileTab, "ask">, V5View> = {
  home: "home",
  plots: "artifacts",
  runs: "workbench",
};

function activeTabFor(activeView: V5View, chatOpen: boolean): V5MobileTab | null {
  if (chatOpen) return "ask";
  if (activeView === "home") return "home";
  if (activeView === "artifacts") return "plots";
  // The workbench surface leads with the run table, so it lives under "Runs".
  if (activeView === "data" || activeView === "workbench") return "runs";
  return null;
}

const TABS: ReadonlyArray<{ id: V5MobileTab; label: string; icon: ReactNode }> = [
  { id: "home", label: "Home", icon: <Icons.Project size={20} /> },
  { id: "plots", label: "Plots", icon: <Icons.SavedPlots size={20} /> },
  { id: "runs", label: "Runs", icon: <Icons.Table size={20} /> },
  { id: "ask", label: "Ask", icon: <Icons.Comments size={20} /> },
];

export function ThreadV5MobileShell({
  projectName,
  activeView,
  chatOpen,
  readOnly,
  onOpenChat,
  onOpenView,
  onSignIn,
  children,
}: ThreadV5MobileShellProps) {
  const activeTab = activeTabFor(activeView, chatOpen);

  const onTab = (tab: V5MobileTab) => {
    if (tab === "ask") {
      // Chat is the mobile interrogation modality. When it isn't available
      // for anonymous share recipients, Ask routes to sign-in rather than
      // dead-ending. Authenticated sessions without live chat stay put.
      if (onOpenChat) onOpenChat();
      else if (readOnly) onSignIn();
      return;
    }
    onOpenView(TAB_TO_VIEW[tab]);
  };

  return (
    <div className="v5-mobile-shell" data-testid="v5-mobile-shell">
      <header className="v5-mobile-header">
        <span className="v5-mobile-brand">
          <Icons.GridIcon size={16} />
          <span className="v5-mobile-title" title={projectName}>{projectName}</span>
        </span>
        {/* FXC-9000: theme toggle reachable in the mobile shell too. */}
        <ThemeToggle />
        {readOnly ? (
          <button className="v5-mobile-signin" type="button" data-testid="v5-mobile-signin" onClick={onSignIn}>
            Sign in
          </button>
        ) : null}
      </header>

      <main className="v5-mobile-content">{children}</main>

      <nav className="v5-mobile-tabbar" aria-label="Thread sections" data-testid="v5-mobile-tabbar">
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              className={`v5-mobile-tab ${isActive ? "is-active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              aria-label={tab.label}
              onClick={() => onTab(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
